/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
    createContext,
    type FormEvent,
    type ReactElement,
    type ReactNode,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { useAppLocation } from '@/hooks/use-app-location';
import { Search as SearchIcon, X as CloseIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isSearchPath } from '@/lib/algolia/constants';
import { parseRouteState, serializeRouteState } from '@/lib/algolia/route-state';

type HeaderSearchContextValue = {
    open: boolean;
    query: string;
    setQuery: (value: string) => void;
    openSearch: () => void;
    closeSearch: () => void;
    submit: (event: FormEvent) => void;
    desktopInputRef: React.RefObject<HTMLInputElement | null>;
    mobileInputRef: React.RefObject<HTMLInputElement | null>;
    inputId: string;
};

const HeaderSearchContext = createContext<HeaderSearchContextValue | null>(null);

function useHeaderSearch(): HeaderSearchContextValue {
    const value = useContext(HeaderSearchContext);
    if (!value) {
        throw new Error('Header search controls must be used inside HeaderSearchProvider');
    }
    return value;
}

export function HeaderSearchProvider({ children }: { children: ReactNode }): ReactElement {
    const location = useAppLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const desktopInputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const inputId = useId();

    useEffect(() => {
        if (!isSearchPath(location.pathname)) return;
        setQuery(new URLSearchParams(location.search).get('q') ?? '');
    }, [location.pathname, location.search]);

    useEffect(() => {
        let width = window.innerWidth;
        const onResize = () => {
            if (window.innerWidth === width) return;
            width = window.innerWidth;
            setOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (target instanceof Element && target.closest('[data-header-search]')) return;
            setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const desktop = window.matchMedia('(min-width: 1024px)').matches;
        (desktop ? desktopInputRef : mobileInputRef).current?.focus();
    }, [open]);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const q = query.trim();
        if (!q) return;
        const current = isSearchPath(location.pathname) ? parseRouteState(location.search) : {};
        const search = serializeRouteState({
            q,
            tab: current.tab ?? 'products',
            sort: isSearchPath(location.pathname) ? current.sort : undefined,
            refinementList: isSearchPath(location.pathname) ? current.refinementList : undefined,
        });
        void navigate({ pathname: '/search', search });
        setOpen(false);
    };

    return (
        <HeaderSearchContext.Provider
            value={{
                open,
                query,
                setQuery,
                openSearch: () => setOpen(true),
                closeSearch: () => setOpen(false),
                submit,
                desktopInputRef,
                mobileInputRef,
                inputId,
            }}>
            {children}
        </HeaderSearchContext.Provider>
    );
}

function SearchField({
    inputRef,
    inputId,
    className,
}: {
    inputRef: React.RefObject<HTMLInputElement | null>;
    inputId: string;
    className?: string;
}) {
    const { t } = useTranslation('header');
    const { query, setQuery, submit, closeSearch } = useHeaderSearch();

    return (
        <form onSubmit={submit} className={cn('flex min-w-0 flex-1 items-center', className)} role="search">
            <input
                ref={inputRef}
                id={inputId}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('searchPlaceholder')}
                aria-label={t('searchPlaceholder')}
                className="h-9 min-w-0 flex-1 bg-transparent px-3 text-sm text-header-foreground outline-none placeholder:text-muted-foreground focus:placeholder:text-transparent"
            />
            <Button
                type="submit"
                variant="ghost"
                size="icon"
                aria-label={t('searchPlaceholder')}
                className="text-header-foreground">
                <SearchIcon />
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('closeSearch')}
                onClick={closeSearch}
                className="text-header-foreground">
                <CloseIcon />
            </Button>
        </form>
    );
}

/** Icon in the header icon row. On desktop the field grows to the left of the icon. */
export function HeaderSearchDesktop(): ReactElement {
    const { t } = useTranslation('header');
    const { open, openSearch, desktopInputRef, inputId } = useHeaderSearch();

    return (
        <div
            data-header-search
            data-testid="header-search-desktop"
            className={cn('flex items-center min-w-0', open ? 'max-lg:hidden lg:flex flex-1' : '')}>
            {open ? (
                <SearchField inputRef={desktopInputRef} inputId={`${inputId}-desktop`} />
            ) : (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-header-foreground"
                    aria-label={t('searchPlaceholder')}
                    aria-expanded={false}
                    onClick={openSearch}>
                    <SearchIcon />
                </Button>
            )}
        </div>
    );
}

/** Full-width field under the header row on small screens. */
export function HeaderSearchMobile(): ReactElement | null {
    const { open, mobileInputRef, inputId } = useHeaderSearch();
    if (!open) return null;

    return (
        <div data-header-search data-testid="header-search-mobile" className="pt-4 lg:hidden">
            <SearchField inputRef={mobileInputRef} inputId={`${inputId}-mobile`} className="border-b border-border" />
        </div>
    );
}
